import {defaultTo, includes} from "lodash-es";
import type {getUnitsJsonSuccess} from "modules/actions/units";
import {SKIP_ERROR_CHECKING} from "modules/constants";
import {TvNetwork, UnitStatusType} from "modules/enums";
import type {getPlayers} from "modules/selectors/players";
import type {getSquadsById} from "modules/selectors/squads";
import type {getScheduledPlayerMatchesSelector} from "modules/selectors/units";

/**
 * Checks if the match has been locked.
 * Playing
 * Complete
 * Canceled
 * @param match
 * @var match.status
 */
export const isUnitLocked = (match?: ReturnType<typeof getUnitsJsonSuccess>["payload"][number]) => {
	return (
		!!match &&
		includes(
			[UnitStatusType.PLAYING, UnitStatusType.COMPLETE, UnitStatusType.CANCELED],
			match?.status
		)
	);
};

/**
 * Checks of the match has been locked by time
 * import.meta.env.VITE_SKIP_ERROR_CHECKING === 'true' can disable this check.
 * @param match
 */
export const isUnitLockedByTime = (
	match?: ReturnType<typeof getUnitsJsonSuccess>["payload"][number]
) => {
	if (!match) {
		return false;
	}
	const startDateOfMatch = new Date(match.lockDateTime).getTime();
	const isLockedByTime = new Date().getTime() > startDateOfMatch;

	return isLockedByTime && !SKIP_ERROR_CHECKING;
};

export const isUnitScheduled = (
	match?: ReturnType<typeof getUnitsJsonSuccess>["payload"][number]
) => {
	return !!match && includes([UnitStatusType.SCHEDULED], match?.status);
};

export const isUnitPlaying = (
	match?: ReturnType<typeof getUnitsJsonSuccess>["payload"][number]
) => {
	return !!match && includes([UnitStatusType.PLAYING], match?.status);
};

/**
 * Returns chance of lineup object. It's multiplied by 100 to get correct value as per design.
 * @param lineupObject
 */
export const getChance = (
	lineupObject?: ReturnType<typeof getUnitsJsonSuccess>["payload"][number]["lineups"][number]
) => {
	if (lineupObject?.starter) {
		const TO_HUNDRED = 100;
		return Number((lineupObject?.starter * TO_HUNDRED).toFixed(1));
	}
	return 0;
};

/**
 * Returns lineupNumber of lineup object. It's divided by 100 to get correct value as per design.
 * @param lineupObject
 */
export const getLineupNumber = (
	lineupObject?: ReturnType<typeof getUnitsJsonSuccess>["payload"][number]["lineups"][number]
): number => {
	if (lineupObject) {
		const lineupNumber = lineupObject.lineupNumber;

		if (!lineupNumber) {
			return 0;
		}
		const HUNDRED = 100;
		return lineupNumber >= HUNDRED ? +(lineupNumber / HUNDRED).toFixed(0) : lineupNumber;
	}

	return 0;
};

export const getTvNetwork = (match?: ReturnType<typeof getUnitsJsonSuccess>["payload"][number]) => {
	if (match?.tvNetwork) {
		return match?.tvNetwork;
	}
	return match?.isFree ? TvNetwork.MLBTV : null;
};

export const getMatchUpPrefix = (isHome: boolean) => {
	return isHome ? "vs" : "@";
};

/**
 * Prepare match up string in format
 * vs TOR, Hutchison (11.57) DH
 */
// eslint-disable-next-line complexity
export const getMatchUp = (
	squads: ReturnType<typeof getSquadsById>,
	players: ReturnType<typeof getPlayers>,
	squadId: number,
	match?: ReturnType<ReturnType<typeof getScheduledPlayerMatchesSelector>>[number],
	roundMatches?: ReturnType<ReturnType<typeof getScheduledPlayerMatchesSelector>>
) => {
	const isHome = match?.homeSquadId === squadId;
	const isDoubleMatch = roundMatches && roundMatches.length > 1;
	const doubleHeaderSuffix = isDoubleMatch ? "DH" : "";

	if (!match) {
		return "";
	}

	const {prefix, pitcherId, squadObject} = isHome
		? {
				pitcherId: defaultTo(match.awayProbablePitcherId, 0),
				squadObject: squads[match.awaySquadId],
				prefix: getMatchUpPrefix(true),
		  }
		: {
				pitcherId: defaultTo(match.homeProbablePitcherId, 0),
				squadObject: squads[match.homeSquadId],
				prefix: getMatchUpPrefix(false),
		  };

	const squadPart = `${prefix} ${squadObject?.abbreviation?.toUpperCase()}`;

	if (players[pitcherId]) {
		const pitcherObject = players[pitcherId];
		const pitcherERA = defaultTo(pitcherObject?.stats?.earnedRunAverage, 0).toFixed(2);

		const pitcherPart = `${pitcherObject?.lastName} (${pitcherERA})`;

		return `${squadPart}, ${pitcherPart} ${doubleHeaderSuffix}`;
	}

	return `${squadPart} ${doubleHeaderSuffix}`;
};
